import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import SectionHeader from "./SectionHeader";
import Drawer from "./Drawer";

const CREDITS = [
  { role: "A&R",                        name: 'Gary "Bigface" Bien-Aime, Lenny S., Ramses Francois' },
  { role: "A&R [Administration]",       name: 'Patrick "Plain Pat" Reynolds' },
  { role: "A&R [Direction]",            name: 'Kyambo "Hip Hop" Joshua' },
  { role: "Administrator",              name: "Terese Joseph" },
  { role: "Art Direction, Design, Cover", name: "Tia Johnson" },
  { role: "Executive-Producer",         name: 'Damon Dash, Kareem "Biggs" Burke, Shawn Carter' },
  { role: "Legal [Business Affairs]",   name: "Antoinette Trotman, Ian Allen (7), Randy McMillan" },
  { role: "Management",                 name: "Erving Wonder Management" },
  { role: "Marketing",                  name: "Andrea Duncan, Emmanuelle Cuny-Panicker" },
  { role: "Mastered By",                name: "Tony Dawsey" },
  { role: "Mixed By",                   name: 'Gimel "Young Guru" Keaton (tracks: 1, 2, 4, 5, 7, 9, 12, 13, 16)' },
  { role: "Photography By",             name: "Jonathan Mannion" },
  { role: "Stylist",                    name: "Monica Morrow" },
  { role: "Written-By",                 name: "L. Pridgen" },
];

const COMPANIES = [
  ["Phonographic Copyright \u2117", "Roc-A-Fella Records, LLC"],
  ["Copyright \u00A9",              "Roc-A-Fella Records, LLC"],
  ["Record Company",                "Criminal Background Records"],
  ["Manufactured By",               "Island Def Jam Music Group"],
  ["Marketed By",                   "Roc-A-Fella Records"],
  ["Marketed By",                   "Island Def Jam Music Group"],
  ["Distributed By",                "Universal Music & Video Distribution, Corp."],
  ["Mastered At",                   "Masterdisk"],
  ["Produced For",                  "F.O.B. Entertainment"],
  ["Produced For",                  "N.Q.C. Management"],
  ["Recorded At",                   "Baseline Studios"],
  ["Produced For",                  "Loreal Inc."],
  ["Recorded At",                   "Quad Recording Studios"],
  ["Produced For",                  "Blessed One Entertainment"],
  ["Mixed At",                      "Quad Recording Studios"],
  ["Published By",                  "Print Music Co., Inc."],
  ["Licensed From",                 "RCA Records"],
  ["Mixed At",                      "Right Track Recording"],
  ["Published By",                  "Unichappell Music, Inc."],
  ["Published By",                  "Alexscar Music"],
  ["Licensed From",                 "Universal Music Enterprises"],
  ["Licensed Through",              "UMG Recordings, Inc."],
  ["Produced For",                  "One Shot Deal Inc."],
  ["Recorded At",                   "Circle House Studios"],
  ["Recorded At",                   "Sound On Sound, New York"],
  ["Mixed At",                      "Sound On Sound, New York"],
  ["Published By",                  "Chi Sound Music"],
  ["Published By",                  "Warner-Tamerlane Publishing Corp."],
  ["Produced For",                  "Roc The World"],
  ["Produced For",                  "PB Productions (2)"],
  ["Produced For",                  "E-Bass Music"],
  ["Produced For",                  "Pat 'Em Down, Inc."],
  ["Published By",                  "Three Wise Boys Music LLC"],
  ["Published By",                  "Embassy Music Corp."],
  ["Produced For",                  "Konman Productions"],
  ["Recorded At",                   "Larrabee North"],
  ["Mixed At",                      "Larrabee North"],
  ["Licensed From",                 "Atlantic Recording Corporation"],
  ["Licensed Through",              "Warner Special Products"],
  ["Produced For",                  "Black Key Productions, Inc."],
  ["Mixed At",                      "Sony Music Studios, New York City"],
  ["Published By",                  "Jobete Music Co., Inc."],
  ["Published By",                  "Dickiebird Music"],
  ["Licensed From",                 "Capitol Records"],
  ["Published By",                  "EMI Unart Catalog Inc."],
  ["Produced For",                  "MonarC Entertainment"],
  ["Engineered At",                 "Capri Digital Studios"],
  ["Engineered At",                 "Atlantis Studios, Los Angeles"],
];
const IDENTIFIERS = [
  ["Barcode (Text)",     "7 31458 69202 7"],
  ["Barcode (Scanned)",  "731458692027"],
  ["ASIN",               "B000088E7G"],
  ["Rights Society",     "BMI"],
  ["Rights Society",     "ASCAP"],
  ["Rights Society",     "SESAC"],
  ["Matrix / Runout",    "314 586 9202 01% D"],
  ["Mastering SID Code", "IFPI L007"],
  ["Mould SID Code",     "IFPI 0368"],
];
const SAMPLES = [
  ["Track #1",  'contains a sample of "Freeway Song" by Vicki Sue Robinson.'],
  ["Track #2",  'contains a sample from "I Just Can\'t See Myself Without You" by Creative Source.'],
  ["Track #3",  'contains a sample of "I Want To Go Back" by The Impressions.'],
  ["Track #7",  'contains a sample of "To Get Love You Must Give Love" by Ultimate Music Experience.'],
  ["Track #8",  'contains an interpolation of "Life For The Taking" by Eddie Money'],
  ["Track #11", 'contains a sample of "We Belong Together" by The Spinners.'],
  ["Track #12", 'contains a sample of "Inside My Love" by Minnie Ripperton.'],
  ["Track #13", 'contains an interpolation of "Mystic Brew" written by R. Foster.'],
];

function Accordion({ id, title, children }) {
  const [open, setOpen] = useState(false); // all closed by default
  return (
    <div className="border-t border-white/10">
      <button
        data-testid={`accordion-${id}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between py-6 md:py-8 text-left hover:text-[#F4C10A] transition-colors group"
      >
        <span className="font-wide text-2xl md:text-3xl uppercase">{title}</span>
        <ChevronDown className={`w-5 h-5 text-white/40 group-hover:text-[#F4C10A] transition-transform ${open ? "rotate-180 text-[#F4C10A]" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="overflow-hidden">
            <div className="pb-10 pt-2">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
function Row({ role, name }) {
  return (
    <div className="grid grid-cols-12 gap-4 py-3 border-b border-white/10">
      <div className="col-span-12 md:col-span-4 font-mono text-[10px] tracking-[0.25em] uppercase text-[#F4C10A]">{role}</div>
      <div className="col-span-12 md:col-span-8 font-body text-sm md:text-base text-white/85">{name}</div>
    </div>
  );
}

export default function OperationPhillyFreeway() {
  const [showBack, setShowBack] = useState(false);
  return (
    <section id="operation" data-testid="operation-section" className="relative bg-[#0A0A0A] text-white overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" />
      <SectionHeader src="/assets/headers/Operation_Header_FINAL.jpg" alt="Operation Philadelphia Freeway" fadeTo="black" />

      <div className="relative px-4 md:px-10 max-w-[1400px] mx-auto pb-20 md:pb-28 -mt-10">
        <div className="mb-8 max-w-3xl relative z-10">
          <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#F4C10A] mb-3">
            The Credentials Behind Making a Classic
          </div>
          <h2 data-testid="operation-heading" className="font-wide text-white uppercase text-4xl sm:text-5xl md:text-6xl">
            Operation Philadelphia Freeway
          </h2>
          <p className="mt-5 font-body text-white/60 text-base md:text-lg leading-relaxed">
            The full production ledger — credits, companies, barcodes, samples,
            and every fingerprint on the original Roc-A-Fella pressing.
          </p>
        </div>

        <Drawer id="operation">
          <div className="flex flex-col items-center mb-10 md:mb-14 relative z-10">
            <button
              data-testid="album-toggle"
              onClick={() => setShowBack((v) => !v)}
              className="group relative"
              aria-label={showBack ? "Show album front" : "Show album back"}
            >
              <img
                data-testid="operation-album-cover"
                src={showBack ? "/assets/album/back.png" : "/assets/album/front.png"}
                alt="Philadelphia Freeway album cover"
                className="w-52 sm:w-64 md:w-80 h-auto object-contain border border-white/10 transition-transform duration-500 group-hover:scale-[1.01]"
              />
              <div className="mt-3 font-mono text-[9px] tracking-[0.3em] uppercase text-white/50">
                {showBack ? "Tap to view front" : "Tap to view back"}
              </div>
            </button>
          </div>

          <Accordion id="credits" title="Credits">
            <div>{CREDITS.map((c, i) => <Row key={i} {...c} />)}</div>
          </Accordion>
          <Accordion id="companies" title="Companies">
            <div>{COMPANIES.map(([r, n], i) => <Row key={i} role={r} name={n} />)}</div>
          </Accordion>
          <Accordion id="barcodes" title="Barcodes & Other Identifiers">
            <div>{IDENTIFIERS.map(([r, n], i) => <Row key={i} role={r} name={n} />)}</div>
          </Accordion>
          <Accordion id="samples" title="Samples & Interpolations">
            <div>{SAMPLES.map(([r, n], i) => <Row key={i} role={r} name={n} />)}</div>
          </Accordion>
        </Drawer>
      </div>
    </section>
  );
}
